import { useEffect, useState, type ChangeEvent } from 'react'
import {
  formatPlNumber,
  parsePlNumber,
  sanitizeNumericInput,
} from '../../numberFormat'
import { inputCommon } from '../inputCommon'

type Props = {
  id?: string
  value: unknown
  onChange: (value: unknown) => void
  disabled?: boolean
  decimals?: number
  suffix?: string
}

/**
 * Bazowy numeric text input z PL parsing (przecinek/kropka jako separator dziesiętny).
 * Commit do {@code number} on blur; empty string clears do {@code null}. {@code decimals=2}
 * + {@code suffix="zł"} dla CURRENCY. Resync z parent value przy zmianie z zewnątrz.
 */
export function NumericInputBase({ id, value, onChange, disabled, decimals, suffix }: Props) {
  const [raw, setRaw] = useState<string>(() => initialRaw(value, decimals))

  useEffect(() => {
    const incoming = initialRaw(value, decimals)
    setRaw(prev => (sameNumber(prev, incoming, decimals) ? prev : incoming))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRaw(sanitizeNumericInput(e.target.value, decimals !== 0))
  }

  // Na focus zdejmujemy separator tysiecy (spacje) - inaczej sanitize usuwa je przy edycji,
  // co rozjezdza pozycje kursora (znak "wskakuje" na poczatek/koniec). Grupowanie wraca na blur.
  const handleFocus = () => {
    setRaw(prev => prev.replace(/\s/g, ''))
  }

  const commit = () => {
    if (raw.trim() === '') {
      onChange(null)
      return
    }
    const parsed = parsePlNumber(raw)
    if (parsed === null || Number.isNaN(parsed)) return
    const rounded =
      decimals === undefined ? parsed : Math.round(parsed * 10 ** decimals) / 10 ** decimals
    onChange(rounded)
    setRaw(formatPlNumber(rounded, decimals))
  }

  return (
    <div className="relative flex items-center">
      <input
        id={id}
        type="text"
        inputMode="decimal"
        className={[
          inputCommon(disabled),
          suffix ? 'pr-12 text-right tabular-nums' : '',
        ].join(' ')}
        disabled={disabled}
        value={raw}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={commit}
      />
      {suffix && (
        <span className="absolute inset-y-0 right-0 flex items-center pl-2 pr-3 text-xs font-medium text-slate-500 border-l border-slate-200 pointer-events-none select-none">
          {suffix}
        </span>
      )}
    </div>
  )
}

function initialRaw(value: unknown, decimals: number | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'number') return formatPlNumber(value, decimals)
  return String(value)
}

function sameNumber(prevRaw: string, nextRaw: string, decimals: number | undefined): boolean {
  const a = parsePlNumber(prevRaw)
  const b = parsePlNumber(nextRaw)
  if (a === null && b === null) return true
  if (a === null || b === null) return false
  if (Number.isNaN(a) || Number.isNaN(b)) return prevRaw === nextRaw
  if (decimals === undefined) return a === b
  const factor = 10 ** decimals
  return Math.round(a * factor) === Math.round(b * factor)
}
